import { Skeleton } from '@h2hspark/ui';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { ConfirmationCard } from '@/member/confirmation-card';
import { PageBackLink } from '@/member/page-back-link';

import { usePublicEvent } from '../event-discovery/api';
import { eventDetailPath, formatEventDateTime } from '../event-discovery/constants';
import { confirmedLine } from '../event-registrations/constants';

import { useReceipt } from './api';
import { CheckoutSteps } from './checkout-steps-panel';
import { BACK_TO_EVENT_LABEL, GO_TO_EVENT_LABEL, RECEIPT_HEADING, RECEIPT_LEAD } from './constants';
import { EventTermsPanel } from './event-terms-panel';
import { OrderItemisation } from './order-itemisation-card';

/**
 * The receipt — what this member reserved, and what it cost (`MEM-020` AC-3, AC-7).
 *
 * ## Its whole reason for existing in this story is AC-3
 *
 * *"Checkout and the receipt itemise **identically**, in the same order and with the same
 * labels."* A criterion about two screens needs both screens to exist, and the second one is this.
 * It renders `OrderItemisation` — the same component, over the same `MemberOrder` shape, built by
 * the same server-side function that built the checkout's — which is why the two cannot itemise
 * differently. `one-itemisation-two-screens.test.tsx` renders both and compares the markup.
 *
 * ## What this is **not**
 *
 * It is not `MEM-022`. That story owns the Tickets & orders list, the order-detail screen with its
 * status badge and Payment card, and the printable receipt with the H2HSpark mark, Billed to, and
 * the card's last four digits — every one of which is a record of a payment, and no payment can be
 * taken yet. This is the confirmation a member lands on after reserving, carrying the three lines
 * and the terms, and `MEM-022` will grow the rest around the same component.
 *
 * ## The conflict `MEM-022` had to resolve, and how it was resolved
 *
 * `MEM-022` AC-4 and AC-5 both quoted **"Total paid"** for the order detail and the receipt, while
 * the prototype's checkout sheet says **"Total"**. `MEM-020` AC-3 requires one label on both
 * screens, and "Total paid" cannot be it — at checkout nothing has been paid. So both render
 * `TOTAL_LABEL`, which is `Total`, and the disagreement was named in `MEM-020`'s Notes rather than
 * settled from a branch. **Sumit ruled on 2026-08-29: the label is "Total"**, and both criteria
 * were reworded to match. Whichever word a later decision picks, it wins in that one constant.
 */
export function OrderReceiptPage(): React.ReactElement {
  const { eventId } = useParams<{ eventId: string }>();
  const id = eventId ?? '';
  const reader = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const event = usePublicEvent(id);
  const query = useReceipt(id, { enabled: id.length > 0 });

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  if (query.error !== null || query.data === undefined) {
    return (
      <div className="space-y-4">
        <BackToEvent eventId={id} />
        <h1 className="text-2xl">We could not find that reservation</h1>
        <p className="text-sm text-muted-foreground">
          {/* The same answer for "never registered" and "cancelled", which is `MEM-017` AC-6's
              position on the event page. Two sentences here would let a member work out which,
              and the page has nothing to do with either. */}
          You do not hold a place at this event.
        </p>
      </div>
    );
  }

  const { receipt } = query.data;
  const detail = event.data ?? null;
  const when =
    detail === null
      ? null
      : formatEventDateTime({
          startsAt: detail.startsAt,
          endsAt: detail.endsAt,
          timezone: detail.timezone,
          reader,
          style: 'long',
        });

  return (
    <div className="space-y-5 pb-4">
      <CheckoutSteps current="confirmed" />

      {/*
        ── §3.1's confirmed card — TW 44132582 (https://ten243.teamwork.com/app/tasks/44132582) ──

        The prototype's `resDone`, on the free and the paid confirmation alike: a centred card at
        `--radius-xl` over `--shadow-sm`, a 72px round `--success-bg` disc with the check in
        `--success`, *You're going! ✦* at `--text-3xl`, one sentence held to 420px, and one
        gradient action. Before this the confirmation's only way on was *Back to event* at the top
        of the page, which read as leaving rather than as the next step; it is gone now that the
        card carries the action.

        **Go To Event, not *See my matches*.** Matches open shortly before the event (`MEM-025`),
        so on the day somebody reserves, the prototype's button goes to a page with nothing on it
        (`handoff-corrections.md` #155). The event page is where the ticket, Who's coming and,
        later, the matches are. The sentence drops the two phrases that would not be true — see
        `RECEIPT_HEADING`.

        The card itself is `ConfirmationCard`, shared since TW 44261258 with the member app's other
        confirmations.
      */}
      <ConfirmationCard
        heading={RECEIPT_HEADING}
        lead={RECEIPT_LEAD}
        action={{ to: eventDetailPath(id), label: GO_TO_EVENT_LABEL }}
      >
        {/* The reference a member reads and quotes, in the wording `MEM-017` AC-1 already prints
            on the confirmed panel — one number, spelled one way, on both screens. */}
        <p className="mt-5 text-xs text-text-subtle">{confirmedLine(receipt.confirmationCode)}</p>
      </ConfirmationCard>

      {/* AC-3. The same component the checkout renders, over the same lines — inside this
          screen's own card, because the itemiser draws no surface (see its header). The event
          and when it is head the card, as they head the checkout's summary; they were the line
          under the heading, which is now the prototype's sentence. */}
      <div className="rounded-xl border border-border-subtle bg-card p-4 shadow-sm sm:p-5">
        {detail === null || when === null ? null : (
          <div className="mb-4 space-y-1 border-b border-divider pb-3.5">
            <p className="font-brand text-xs font-bold uppercase tracking-caps text-brand-accent">
              {when}
            </p>
            <h2 className="font-brand text-lg font-bold text-foreground">{detail.name}</h2>
          </div>
        )}
        <OrderItemisation order={receipt.order} />
      </div>

      {/* AC-7's second clause, and `EVT-024` AC-2's — the terms **repeated in the confirmation**,
          in the Event Terms and Conditions section the checkout draws (TW 44132872).

          Since `MEM-021` this is the registration's own snapshot: what this member agreed at the
          instant they pressed Pay, rendered by the same `describeRefundPolicy` that renders the
          public listing's sentence, so the two say the same words about one deal. It is *not* the
          event's current policy, and the server's `AgreedRefundPolicy` is what makes handing this
          screen that a type error rather than a mistake somebody has to notice.

          The served notice still stands in for a paid event published before publish-gate row 12
          existed, which is the only registration that can carry no terms.

          `MEM-050` AC-3: the fee-forfeit disclosure the checkout showed, **repeated in the
          confirmation in the same words** — the same served sentence, in the same region, under
          the terms it goes with. */}
      <EventTermsPanel
        policySentence={receipt.refundPolicy?.sentence ?? receipt.refundPolicyNotice ?? ''}
        feeDisclosure={receipt.feeDisclosure}
      />
    </div>
  );
}

function BackToEvent({ eventId }: { readonly eventId: string }): React.ReactElement {
  return (
    <PageBackLink to={`/app/events/${encodeURIComponent(eventId)}`}>
      {BACK_TO_EVENT_LABEL}
    </PageBackLink>
  );
}
